import { useHistory } from '@/router'
import { HeaderSorting } from '@/uikit/HeaderSorting'
import { faListUl, faSlidersH } from '@fortawesome/pro-regular-svg-icons'
import {
  Button,
  Checkbox,
  ColumnsType,
  ColumnType,
  State,
  Toast,
} from '@seiue/ui'
import { map, pick } from '@seiue/util'
import {
  DataTable,
  DataTableProps,
  FormConfig,
  IParams,
} from 'packages/components/DataTable'
import {
  FieldDomainEnum,
  InformalReflection,
  fieldApi,
} from 'packages/sdks-next/growp'
import { AnyType } from 'packages/types'
import React, { useCallback, useState } from 'react'
import styled from 'styled-components'

import { MultiExport } from '../MultiExport'

import { ListContext } from './context'
import { useStudentColumns } from './useColumns'
import {
  formatInformStuDataSource,
  formatInformStuSearchParams,
  getExportConfig,
  getSearchFormConfig,
} from './utils'

/**
 * 生源池|人才池 - 列表
 *
 * @param param - 参数
 * @param param.domain - domain
 * @param param.api - 表格数据api
 * @param param.hiddenFieldSettings - 是否隐藏表头设置
 * @param param.headerRight - 表格头部右侧组件
 * @param param.rowAction - 行处理
 * @param param.exportConfig - 导出配置
 * @param param.fieldSettingLocation - 字段额外设置
 * @param param.showSelectedCheck - 是否显示只看已选
 * @param param.outputSelectedRowKeys - 外部注入本表格选中的列表数据 ID(某些情况，需要外部注入)
 * @param param.setOutputSelectedRowKeys - 导出本表格选中的列数据 ID(某些情况，外部组件需要知道本主键内选中的列数据)
 * @param param.showSelectedAll - 显示选择全部
 * @param param.allowSelect - 表格是否可选
 * @param param.formatColumns - 格式化 columns
 * @param param.formatSearchFormConfig - 格式化搜索配置
 * @param param.formatSearchParams - 格式化搜索参数
 * @param param.scroll - 滚动
 * @returns React.ReactNode
 */
export const List: React.FC<
  {
    domain: FieldDomainEnum
    rowAction: ColumnsType<InformalReflection>
    exportConfig?: {
      downloadFile: (query: string, idIn: string, keys: string) => void
    }
    fieldSettingLocation?: string
    hiddenFieldSettings?: boolean
    showSelectedCheck?: boolean
    outputSelectedRowKeys?: number[]
    setOutputSelectedRowKeys?: (keys: number[]) => void
    allowSelect?: boolean
    formatColumns?: (
      columns: ColumnType<InformalReflection>[]
    ) => ColumnType<InformalReflection>[]
    formatSearchFormConfig?: (config: FormConfig) => FormConfig
    formatSearchParams?: (params: IParams) => IParams
  } & Pick<
    DataTableProps,
    | 'api'
    | 'extraApiDependence'
    | 'apiName'
    | 'headerRight'
    | 'showSelectedAll'
    | 'headerLeft'
    | 'scroll'
  >
> = ({
  domain,
  headerRight,
  rowAction,
  exportConfig,
  fieldSettingLocation,
  hiddenFieldSettings,
  showSelectedCheck,
  outputSelectedRowKeys,
  setOutputSelectedRowKeys,
  showSelectedAll = true,
  allowSelect = true,
  formatColumns,
  formatSearchFormConfig,
  formatSearchParams,
  scroll,
  ...rest
}) => {
  const history = useHistory()
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>(
    outputSelectedRowKeys !== undefined ? outputSelectedRowKeys : []
  )
  const [showDrawer, setShowDrawer] = useState<boolean>(false)
  const [showExport, setShowExport] = useState<boolean>(false)
  const [showSelectedRows, setShowSelectedRows] = useState<boolean>(false)

  const { data: fields, loading } = fieldApi.useQueryFields({
    domain,
    query: {
      expand: ['options'],
    },
  })

  const { data: groupsData } = fieldApi.useQueryFieldGroups({
    domain,
    query: { expand: ['fields'] },
  })

  const { api: updateFields } = fieldApi.useBatchSortField({
    reload: [fieldApi.queryFields, fieldApi.queryFieldGroups],
  })

  const columns = [
    ...useStudentColumns({
      fields: fields || [],
      domain,
      hasLeft: !!hiddenFieldSettings,
    }),
    ...rowAction,
  ]

  const getHeaderData = useCallback(() => {
    const formatFields = fields?.filter((i) => {
      if (
        [FieldDomainEnum.FormalStudent, FieldDomainEnum.FormalTeacher].includes(
          domain
        ) &&
        i.name === 'activity_names'
      ) {
        return false
      } else if (
        [
          FieldDomainEnum.InformalStudent,
          FieldDomainEnum.InformalTeacher,
        ].includes(domain) &&
        (i.name === 'account' || i.name === 'avatar')
      ) {
        return false
      }
      return true
    })
    return map(formatFields || [], (obj) => ({
      ...pick(obj, [
        'id',
        'label',
        'weight',
        'archivedIsShow',
        'archivedWeight',
        'isShow',
      ]),
      disabled: 'name' === obj.name,
    }))
  }, [fields])

  if (loading) return <State.Loading />
  return (
    <ListContext.Provider
      value={{
        domain,
        selectedRowKeys,
        showExport: () => setShowExport(true),
        clearRowKeys: () => setSelectedRowKeys([]),
      }}
    >
      {exportConfig ? (
        <MultiExport
          visible={showExport}
          groupItem={getExportConfig(groupsData, domain)}
          onCancel={() => setShowExport(false)}
          downloadFile={(query: string, keys: string) => {
            const idIn = selectedRowKeys.join(',')
            exportConfig.downloadFile(query, idIn, keys)
          }}
          defaultExportKeys={['name', 'phone']}
        />
      ) : null}
      <HeaderSorting
        hasLeft={hiddenFieldSettings}
        headerData={getHeaderData()}
        visible={showDrawer}
        onClose={() => setShowDrawer(false)}
        onOk={async (payload) => {
          await updateFields({
            domain,
            payload: payload,
          })
          setShowDrawer(false)
          Toast.success('保存成功')
        }}
        visibleControl={true}
      />
      <DataTable
        columns={formatColumns ? formatColumns(columns) : columns}
        {...rest}
        headerRight={
          <HeaderRight>
            {showSelectedCheck ? (
              <Checkbox
                disabled={!selectedRowKeys.length}
                onChange={(e) => setShowSelectedRows(e.target.checked)}
              >
                {'只看已选'}
              </Checkbox>
            ) : null}
            {hiddenFieldSettings ? null : (
              <Button
                icon={faSlidersH}
                onClick={() => {
                  history.push(fieldSettingLocation as AnyType)
                }}
              >
                字段设置
              </Button>
            )}
            <Button
              icon={faListUl}
              onClick={() => setShowDrawer(true)}
            >
              表头管理
            </Button>
            {headerRight}
          </HeaderRight>
        }
        formatData={formatInformStuDataSource}
        formatParams={
          formatSearchParams
            ? (params) =>
                formatSearchParams(formatInformStuSearchParams(params))
            : formatInformStuSearchParams
        }
        searchFormConfig={
          formatSearchFormConfig?.(
            getSearchFormConfig(groupsData || [], domain)
          ) || getSearchFormConfig(groupsData || [], domain)
        }
        isPagination={true}
        selectedRowKeys={allowSelect ? selectedRowKeys : undefined}
        onSelectedRowKeysChange={(keys) => {
          setSelectedRowKeys(keys as number[])
          setOutputSelectedRowKeys?.(keys as number[])
        }}
        showSelectedAll={showSelectedAll}
        scroll={scroll || { x: '1300', y: 'calc(100vh - 348px)' }}
        showSelectedRows={showSelectedRows}
      />
    </ListContext.Provider>
  )
}

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`
